/* eslint-disable unicode-bom */
// Admin Sidebar Component
import React, { createElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiHome, FiUsers, FiUserCheck, FiHeart, FiDollarSign,
  FiFileText, FiActivity, FiImage, FiMail, FiSettings,
} from 'react-icons/fi';

interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const Sidebar: React.FC = () => {
  const location = useLocation();

  const sections: NavSection[] = [
    {
      label: 'MENU',
      items: [
        { id: 'dashboard',  label: 'Dashboard',  icon: 'home',      path: '/admin' },
        { id: 'children',   label: 'Children',   icon: 'heart',     path: '/admin/children' },
        { id: 'team',       label: 'Team',        icon: 'users',     path: '/admin/team' },
        { id: 'volunteers', label: 'Volunteers', icon: 'userCheck', path: '/admin/volunteers' },
      ],
    },
    {
      label: 'MANAGEMENT',
      items: [
        { id: 'donors',     label: 'Donors',     icon: 'dollarSign', path: '/admin/donors' },
        { id: 'content',    label: 'Content',    icon: 'fileText',   path: '/admin/content' },
        { id: 'engagement', label: 'Engagement', icon: 'activity',   path: '/admin/engagement' },
        { id: 'gallery',    label: 'Gallery',    icon: 'image',      path: '/admin/gallery' },
      ],
    },
    {
      label: 'OTHERS',
      items: [
        { id: 'messages', label: 'Messages', icon: 'mail',     path: '/admin/messages' },
        { id: 'settings', label: 'Settings', icon: 'settings', path: '/admin/settings' },
      ],
    },
  ];

  const getIcon = (name: string, active: boolean) => {
    const color = active ? '#03A84A' : '#94A3B8';
    const props = { size: 16, style: { color, flexShrink: 0 } } as any;
    switch (name) {
      case 'home':       return createElement(FiHome      as any, props);
      case 'heart':      return createElement(FiHeart     as any, props);
      case 'users':      return createElement(FiUsers     as any, props);
      case 'userCheck':  return createElement(FiUserCheck as any, props);
      case 'dollarSign': return createElement(FiDollarSign as any, props);
      case 'fileText':   return createElement(FiFileText  as any, props);
      case 'activity':   return createElement(FiActivity  as any, props);
      case 'image':      return createElement(FiImage     as any, props);
      case 'mail':       return createElement(FiMail      as any, props);
      case 'settings':   return createElement(FiSettings  as any, props);
      default: return null;
    }
  };

  const isActive = (path: string) =>
    path === '/admin'
      ? location.pathname === '/admin' || location.pathname === '/admin/dashboard'
      : location.pathname === path;

  return (
    <div style={{
      width: '250px',
      height: '100vh',
      position: 'fixed',
      left: 0, top: 0,
      background: '#FFFFFF',
      borderRight: '1px solid #E8EDF3',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '3px 0 18px rgba(12,34,57,0.045)',
      overflow: 'hidden',
    }}>

      {/* ── Logo ─────────────────────────────── */}
      <div style={{
        padding: '22px 22px 20px',
        borderBottom: '1px solid #F1F5F9',
        display: 'flex',
        alignItems: 'center',
        gap: '13px',
      }}>
        <div style={{
          width: '40px', height: '40px',
          borderRadius: '12px',
          background: 'linear-gradient(145deg, #0C2239 0%, #1a4470 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 4px 14px rgba(12,34,57,0.30)',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 21.593c-5.63-5.539-11-10.297-11-14.402 0-3.791
                 3.068-5.191 5.281-5.191 1.312 0 4.151.501 5.719 4.457
                 1.59-3.968 4.464-4.447 5.726-4.447 2.54 0 5.274 1.621
                 5.274 5.181 0 4.069-5.136 8.625-11 14.402z"
              fill="#03A84A"
            />
          </svg>
        </div>
        <div>
          <div style={{
            color: '#0C2239', fontSize: '13.5px', fontWeight: '700',
            lineHeight: 1.2, letterSpacing: '-0.02em',
          }}>
            New Life Nepal
          </div>
          <div style={{
            color: '#94A3B8', fontSize: '11px', fontWeight: '500', marginTop: '3px',
          }}>
            Admin Dashboard
          </div>
        </div>
      </div>

      {/* ── Navigation ───────────────────────── */}
      <nav style={{ flex: 1, padding: '8px 0 12px', overflowY: 'auto' }}>
        {sections.map((section) => (
          <div key={section.label} style={{ marginBottom: '4px' }}>
            {/* Section label */}
            <div style={{
              padding: '14px 22px 5px',
              fontSize: '10px', fontWeight: '700',
              color: '#B8C5D3',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}>
              {section.label}
            </div>

            {section.items.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`nln-nav-link${active ? ' nln-active' : ''}`}
                  style={{
                    background: active ? 'rgba(3,168,74,0.09)' : 'transparent',
                  }}
                >
                  {/* Icon box */}
                  <div style={{
                    width: '32px', height: '32px',
                    borderRadius: '8px',
                    background: active ? 'rgba(3,168,74,0.13)' : '#F8FAFC',
                    border: active ? '1px solid rgba(3,168,74,0.22)' : '1px solid #F0F4F8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.18s ease',
                  }}>
                    {getIcon(item.icon, active)}
                  </div>

                  {/* Label */}
                  <span style={{
                    color: active ? '#0C2239' : '#64748B',
                    fontSize: '13px',
                    fontWeight: active ? '600' : '500',
                    letterSpacing: '-0.01em',
                  }}>
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* ── Footer ───────────────────────────── */}
      <div style={{
        padding: '12px 22px',
        borderTop: '1px solid #F1F5F9',
        background: '#FAFBFC',
      }}>
        <p style={{
          margin: 0, fontSize: '10.5px', color: '#B8C5D3',
          textAlign: 'center', fontWeight: '500',
        }}>
          © 2026 New Life Nepal
        </p>
      </div>
    </div>
  );
};

export default Sidebar;
